import { Link } from "react-router-dom";
import { Mail, Phone, MapPin } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { usePublicFooterContent, usePublicNavigationLinks, usePublicContactContent } from "@/hooks/api/use-public";
import { useSiteSettings } from "@/hooks/api/use-site-settings";
import { Skeleton } from "@/components/ui/skeleton";
import { getImageUrl } from "@/lib/image-utils";
import { useMemo } from "react";

const Footer = () => {
  const { t, isRTL, language } = useLanguage();
  const { data: footerContent, isLoading: isLoadingFooter } = usePublicFooterContent();
  const { data: navigationLinksData, isLoading: isLoadingNavLinks } = usePublicNavigationLinks();
  const { data: contactContent } = usePublicContactContent();
  const { data: siteSettings, isLoading: isLoadingSettings } = useSiteSettings();

  // API returns { success: true, data: { content: {...}, quick_links: [...] } }
  const content = footerContent?.data?.content;

  // Use logo from site settings, fallback to footer content, then local asset
  // Add timestamp for cache busting when logo changes
  const logoSrc = useMemo(() => {
    const url = siteSettings?.data?.logo || content?.company_logo;
    return getImageUrl(url);
  }, [siteSettings?.data?.logo, content?.company_logo]);

  const companyDescription = content?.company_description
    ? (typeof content.company_description === 'string'
        ? content.company_description
        : content.company_description[language] || content.company_description.en)
    : null;

  // For section titles, use fallback values if API returns URLs instead of text
  const quickLinksTitle = content?.quick_links_title
    ? (typeof content.quick_links_title === 'string' && (content.quick_links_title as string).startsWith('http')
        ? null  // Use shimmer if it's a URL
        : (typeof content.quick_links_title === 'string'
            ? content.quick_links_title
            : content.quick_links_title[language] || content.quick_links_title.en))
    : null;

  const legalTitle = content?.legal_title
    ? (typeof content.legal_title === 'string' && (content.legal_title as string).startsWith('http')
        ? null  // Use shimmer if it's a URL
        : (typeof content.legal_title === 'string'
            ? content.legal_title
            : content.legal_title[language] || content.legal_title.en))
    : null;

  const contactTitle = content?.contact_title
    ? (typeof content.contact_title === 'string' && (content.contact_title as string).startsWith('http')
        ? null  // Use shimmer if it's a URL
        : (typeof content.contact_title === 'string'
            ? content.contact_title
            : content.contact_title[language] || content.contact_title.en))
    : null;

  // Get contact info from Footer API, fallback to Contact API if not available
  const phone = content?.phone || contactContent?.data?.phone || null;
  const email = content?.email || contactContent?.data?.email || null;
  
  // Location from Footer API - if it's a URL, make it clickable
  const footerLocation = content?.location
    ? (typeof content.location === 'string'
        ? content.location
        : content.location[language] || content.location.en)
    : null;
  
  // Location from Contact API (fallback)
  const contactLocation = contactContent?.data?.address
    ? (typeof contactContent.data.address === 'string'
        ? contactContent.data.address
        : contactContent.data.address[language] || contactContent.data.address.en)
    : null;
  
  const location = footerLocation || contactLocation || null;

  const copyrightText = content?.copyright_text || null;

  const allNavigationLinks = navigationLinksData?.data?.filter(link => link.is_active).sort((a, b) => a.order - b.order) || [];
  
  // Filter navigation links by route_name or category
  // Quick Links: links without route_name or route_name !== 'legal'
  const navigationLinks = allNavigationLinks.filter(link => !link.route_name || link.route_name !== 'legal');
  
  // Legal Links: links with route_name === 'legal'
  const legalLinks = allNavigationLinks.filter(link => link.route_name === 'legal');

  return (
    <footer className="bg-secondary text-secondary-foreground">
      <div className="container mx-auto px-4 py-16">
        {isLoadingFooter ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {[1, 2, 3, 4].map((i) => (
              <Skeleton key={i} className="h-64" />
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">
            {/* Logo & About */}
            <div className="space-y-4">
              {isLoadingSettings || !logoSrc ? (
                <div className="h-16 w-32 animate-pulse bg-gradient-to-r from-slate-200 via-slate-100 to-slate-200 bg-[length:200%_100%] rounded-lg" 
                     style={{ animation: 'shimmer 1.5s infinite' }} />
              ) : (
                <img 
                  src={logoSrc} 
                  alt="KYNZ Contracting" 
                  className="h-16 w-auto object-contain"
                />
              )}
              {companyDescription ? (
                <p className="text-secondary-foreground/80 text-sm leading-relaxed">
                  {companyDescription}
                </p>
              ) : (
                <Skeleton className="h-16 w-full" />
              )}
            </div>

            {/* Quick Links */}
            <div>
              <h4 className="text-lg font-bold text-primary mb-4">
                {quickLinksTitle || <Skeleton className="h-6 w-24 inline-block" />}
              </h4>
              <ul className="space-y-3">
                {isLoadingNavLinks ? (
                  <>
                    <li><Skeleton className="h-4 w-20" /></li>
                    <li><Skeleton className="h-4 w-24" /></li>
                    <li><Skeleton className="h-4 w-28" /></li>
                  </>
                ) : navigationLinks.length > 0 ? (
                  navigationLinks.map((link) => {
                    const label = typeof link.label === 'string'
                      ? link.label
                      : (link.label?.[language] || link.label?.en || '');
                    
                    return (
                      <li key={link.id}>
                        {link.is_external ? (
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                          >
                            {label}
                          </a>
                        ) : (
                          <Link 
                            to={link.url} 
                            className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                          >
                            {label}
                          </Link>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li className="text-sm text-secondary-foreground/60">{t('footer.noLinks') || 'لا توجد روابط'}</li>
                )}
              </ul>
            </div>

            {/* Legal */}
            <div>
              <h4 className="text-lg font-bold text-primary mb-4">
                {legalTitle || <Skeleton className="h-6 w-20 inline-block" />}
              </h4>
              <ul className="space-y-3">
                {isLoadingNavLinks ? (
                  <>
                    <li><Skeleton className="h-4 w-20" /></li>
                    <li><Skeleton className="h-4 w-24" /></li>
                  </>
                ) : legalLinks.length > 0 ? (
                  legalLinks.map((link) => {
                    const label = typeof link.label === 'string'
                      ? link.label
                      : (link.label?.[language] || link.label?.en || '');
                    
                    return (
                      <li key={link.id}>
                        {link.is_external ? (
                          <a 
                            href={link.url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                          >
                            {label}
                          </a>
                        ) : (
                          <Link 
                            to={link.url} 
                            className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                          >
                            {label}
                          </Link>
                        )}
                      </li>
                    );
                  })
                ) : (
                  <li className="text-sm text-secondary-foreground/60"></li>
                )}
              </ul>
            </div>

            {/* Contact */}
            <div>
              <h4 className="text-lg font-bold text-primary mb-4">
                {contactTitle || <Skeleton className="h-6 w-24 inline-block" />}
              </h4>
              <ul className="space-y-3">
                <li className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                  <Phone size={16} className="text-primary flex-shrink-0" />
                  {phone ? <span dir="ltr">{phone}</span> : <Skeleton className="h-4 w-28" />}
                </li>
                <li className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                  <Mail size={16} className="text-primary flex-shrink-0" />
                  {email ? <span>{email}</span> : <Skeleton className="h-4 w-32" />}
                </li>
                <li className="flex items-center gap-3 text-sm text-secondary-foreground/80">
                  <MapPin size={16} className="text-primary flex-shrink-0" />
                  {footerLocation && footerLocation.startsWith('http') ? (
                    <a 
                      href={footerLocation} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      className="text-sm text-secondary-foreground/80 hover:text-primary transition-colors"
                    >
                      {t('footer.viewLocation') || 'عرض الموقع'}
                    </a>
                  ) : location ? (
                    <span>{location}</span>
                  ) : (
                    <Skeleton className="h-4 w-36" />
                  )}
                </li>
              </ul>
            </div>
          </div>
        )}

        <div className="mt-12 pt-8 border-t border-secondary-foreground/20 text-center">
          <p className="text-sm text-secondary-foreground/60">
            {isLoadingFooter || !copyrightText ? (
              <Skeleton className="h-4 w-64 inline-block" />
            ) : (
              copyrightText
            )}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
